import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Manual types until API client is properly generated with ProducesResponseType attributes
export enum NotificationType {
  AssignmentStart = 1,
  Reminder24h = 2,
  Overdue = 3,
  Announcement = 4,
  CourseUpdate = 5,
  GradePosted = 6,
  MessageReceived = 7,
  SystemAlert = 8,
}

export enum RelatedEntityType {
  None = 0,
  AssignmentDefinition = 1,
  Course = 2,
  Announcement = 3,
  Message = 4,
  System = 5,
}

export interface Notification {
  id: number;
  userId: number;
  accountId: number;
  notificationType: NotificationType;
  title: string;
  htmlBody: string;
  textBody: string;
  isRead: boolean;
  readAt?: string;
  relatedEntityType?: RelatedEntityType;
  relatedEntityId?: number;
  createdAt: string;
  sentAt?: string;
  scheduledFor?: string;
  metadata?: string;
}

export interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  lastFetchTime: number | null;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  lastFetchTime: null,
};

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.isRead) {
        state.unreadCount += 1;
      }
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    markAsRead: (state, action: PayloadAction<number[]>) => {
      const idsToMark = new Set(action.payload);
      state.notifications = state.notifications.map((notif) =>
        idsToMark.has(notif.id) ? { ...notif, isRead: true } : notif
      );
      state.unreadCount = Math.max(0, state.unreadCount - action.payload.length);
    },
    updateLastFetchTime: (state) => {
      state.lastFetchTime = Date.now();
    },
  },
});

export const {
  setNotifications,
  addNotification,
  setUnreadCount,
  markAsRead,
  updateLastFetchTime,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
